import React, { useEffect, useRef, useState, useMemo } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { useTabs } from "../contexts/TabsContext";

/**
 * TabPagesRouter — Replaces the standard <Routes> rendering for the protected
 * inner area. Each page that has ever been opened in a tab is rendered ONCE
 * and kept mounted. Only the active tab is shown via CSS (display:block);
 * the others stay alive in the background, preserving scroll position,
 * input state, fetched data, etc.
 *
 * When a tab is closed (via TabsBar), the corresponding page is unmounted on
 * the next render cycle (auto-cleanup via tabs array).
 *
 * Props:
 *   routeMap : Array<{ pattern: string, element: ReactElement }>
 *     - pattern: react-router path pattern (e.g. "/space/:spaceId")
 *     - element: JSX to render when this route is active
 */
export default function TabPagesRouter({ routeMap }) {
    const location = useLocation();
    const { tabs } = useTabs();
    const currentPath = location.pathname + location.search;

    // Keep track of which (tab.id, route) pairs we've mounted.
    // We mount once per tab and keep mounted until the tab is closed.
    const mountedRef = useRef(new Map()); // Map<tabId, {route, element}>

    // Add current route to mountedRef if missing (covers initial load before
    // TabsContext auto-creates the tab).
    const activeTab = useMemo(() => tabs.find((t) => t.route === currentPath), [tabs, currentPath]);
    const fallbackTabId = "__direct__";

    // Build the list of pages to render: one per open tab.
    const pages = useMemo(() => {
        const list = tabs.map((t) => ({ tabId: t.id, route: t.route }));
        // If the current path isn't in tabs (e.g. initial load), add a fallback
        if (!activeTab && currentPath) {
            list.push({ tabId: fallbackTabId, route: currentPath });
        }
        return list;
    }, [tabs, activeTab, currentPath]);

    // Resolve a route string to its matching element from routeMap.
    const resolveElement = (route) => {
        const pathname = route.split("?")[0];
        for (const r of routeMap) {
            if (matchPath(r.pattern, pathname)) return r.element;
        }
        // Default fallback
        return routeMap[0]?.element || null;
    };

    // Garbage-collect mountedRef entries whose tabId is no longer in `pages`.
    const liveIds = new Set(pages.map((p) => p.tabId));
    for (const tabId of Array.from(mountedRef.current.keys())) {
        if (!liveIds.has(tabId)) {
            mountedRef.current.delete(tabId);
        }
    }

    // Mount each new tab once.
    for (const p of pages) {
        if (!mountedRef.current.has(p.tabId)) {
            mountedRef.current.set(p.tabId, { route: p.route, element: resolveElement(p.route) });
        }
    }

    // Determine which tabId is currently active for visibility.
    const activeTabId = activeTab?.id || fallbackTabId;

    return (
        <>
            {Array.from(mountedRef.current.entries()).map(([tabId, { route, element }]) => (
                <div
                    key={tabId}
                    style={{ display: tabId === activeTabId ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}
                    data-tab-page={tabId}
                    data-route={route}
                    data-active={tabId === activeTabId ? "true" : "false"}
                >
                    {element}
                </div>
            ))}
        </>
    );
}
